import { Modal } from "@/components/ui/Modal";
import { useEffect, useId, useRef } from "react";
import { Button } from "@/components/ui/button";

type Props = {
  title: string;
  description: string;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  pending?: boolean;
  destructive?: boolean;
  error?: string;
  inline?: boolean;
};

export function ConfirmDialog({
  title,
  description,
  open,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  pending = false,
  destructive = false,
  error,
  inline = false,
}: Props) {
  const titleId = useId();
  const inlineRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open || !inline) return;
    const previous = document.activeElement as HTMLElement | null;
    inlineRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => previous?.focus();
  }, [open, inline]);
  if (!open) return null;

  const content = <>
      <h2 id={titleId} className="text-xl font-semibold text-white">
        {title}
      </h2>

      <p className="mt-2 text-slate-400">{description}</p>

      {error && <p role="alert" className="mt-3 text-sm text-rose-300">{error}</p>}

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>

        <Button variant={destructive ? "danger" : "primary"} disabled={pending} onClick={onConfirm}>
          {pending ? "Saving…" : confirmText}
        </Button>
      </div>
    </>;
  return inline ? <section ref={inlineRef} aria-labelledby={titleId}>{content}</section> : <Modal open={open} onClose={() => { if (!pending) onCancel(); }} ariaLabelledBy={titleId} panelClassName="max-w-md">{content}</Modal>;
}
